// This component is responsible for displaying and managing the list of users.

import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import {
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
  TUser,
} from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import DateUtils from "../../../utils/date";
import {
  eColumnConfigKeys,
  ePrivileges,
  eResultCode,
  eRoleUniqueId,
} from "../../../utils/enum";
import styles from "./users.module.css";
import UserForm from "./form";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  CONFIG_GROUPS,
  dateOnlyFormat,
  defaultFilterParams,
  PageSize,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import GenericPopover from "../../../components/pop-over";
import {
  Button,
  Tag,
  Space,
  Input,
  Table,
  Drawer,
  TablePaginationConfig,
  Tooltip,
  Dropdown,
  Modal,
} from "antd";
import CustomDrawer from "../../../components/drawer";
import {
  AddEditUser,
  GetConfigParamDataList,
  GetUserList,
} from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import utils from "../../../utils";
import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  CheckOutlined,
  TeamOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/export-to-csv/export-to-csv";
import { ColumnConfigManager } from "../../../utils/column-management";
import BulkUploadUserForm from "./bulk-upload";

/*
 * UserList Component
 * This component is responsible for displaying and managing the list of users.
 * It handles fetching user data, searching, filtering, and rendering the user list.
 */
export default function UserList() {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TUser[]>([]);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const context: any = useContext(UserContext);
  const [selectedUser, setSelectedUser] = useState<TUser | null>(null); // State to keep track of selected user for modal

  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: PageSize,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload,
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  const showDeleteModal = (user: TUser) => {
    setSelectedUser(user);
    setIsDeleteModalVisible(true);
  };

  const deleteUser = async (user: TUser) => {
    try {
      const payload = {
        data: {
          id: user.id,
          isDeleted: 1,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const showModal = (user: TUser) => {
    setSelectedUser(user);
    setIsModalVisible(true);
  };

  const handleOk = (userDetails: TUser) => {
    updateUserDetails(userDetails);
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setIsModalVisible(false);
  };

  const handleDeleteOk = async () => {
    if (selectedUser) {
      await deleteUser(selectedUser);
    }
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setSelectedUser(null);
    setIsDeleteModalVisible(false);
  };

  const initialColumns: any[] = [
    {
      title: "Sr. No.",
      key: "serialNumber",
      width: "8%",
      render: (_: any, __: TUser, index: number) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "fullName", // Ensure this key exists in TUser
      key: "fullName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "fullName"),
      sorter: (a: TUser, b: TUser) =>
        a.fullName?.localeCompare(b.fullName ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.fullName?.includes(value as string),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      width: "30%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.emailId?.localeCompare(b.emailId ?? "") || 0,
      filters: utils.getUniqueFilters(data, "emailId"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.emailId?.includes(value as string),
    },
    {
      title: "Mobile No.",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.mobileNumber?.localeCompare(b.mobileNumber ?? "") || 0,
      filters: utils.getUniqueFilters(data, "mobileNumber"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.mobileNumber?.includes(value as string),
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.roleName?.localeCompare(b.roleName ?? "") || 0,
      filters: utils.getUniqueFilters(data, "roleName"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.roleName?.includes(value as string),
    },
    {
      title: "Designation",
      dataIndex: "designationTitle",
      key: "designationTitle",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        a.designationTitle?.localeCompare(b.designationTitle ?? "") || 0,
      filters: utils.getUniqueFilters(data, "designationTitle"),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.designationTitle?.includes(value as string),
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: "15%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      onFilter: (value: string | number | boolean, record: TUser) =>
        record.createdOn &&
        record.createdOn.toString().includes(value as string),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, dateOnlyFormat); // Apply date format here
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      width: "10%",
      filterSearch: true,
      sorter: (a: TUser, b: TUser) => (a.isActive ?? 0) - (b.isActive ?? 0),
      onFilter: (value: number | boolean, record: TUser) =>
        record.isActive === value,
      render: (isActive: number) => (
        <Tag
          color={isActive === 1 ? "#559c6aff" : "#ef4450"}
          className={styles.statusTag}
        >
          {isActive === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "8%",
      render: (user: TUser) => (
        <>
          {/* {user.roleUniqueId && String(user.roleUniqueId) !== eRoleUniqueId.ADMIN && ( */}
          <Dropdown
            trigger={["click"]}
            getPopupContainer={() => document.body}
            menu={{
              items: [
                Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITUSER,
                ) && {
                  key: "edit",
                  label: (
                    <CustomDrawer
                      placement="right"
                      size="large"
                      trigger={
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          <EditOutlined style={{ fontSize: "12px" }} />
                          Edit
                        </div>
                      }
                      component={UserForm}
                      componentProps={{
                        id: user.id,
                        onRefreshList: () => {
                          fetchData();
                        },
                      }}
                      title="Edit User"
                    />
                  ),
                },
                Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.DELETEUSER,
                ) && {
                  key: "activate_deactivate",
                  label: (
                    <div
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                      onClick={() => showModal(user)}
                    >
                      {user.isActive ? (
                        <UserDeleteOutlined style={{ fontSize: "12px" }} />
                      ) : (
                        <UserAddOutlined style={{ fontSize: "12px" }} />
                      )}
                      {user.isActive ? "Inactive" : "Active"}
                    </div>
                  ),
                },
                Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.DELETEUSER,
                ) && {
                  key: "delete",
                  label: (
                    <div
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                      onClick={() => showDeleteModal(user)}
                    >
                      <DeleteOutlined style={{ fontSize: "12px" }} />
                      Delete
                    </div>
                  ),
                },
              ].filter(Boolean) as any,
            }}
          >
            <MoreOutlined style={{ fontSize: 18, cursor: "pointer" }} />
          </Dropdown>
        </>
      ),
    },
  ];
  console.log("context", context);
  useEffect(() => {
    fetchData();
  }, [context]);

  // const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [columns, setColumns] = useState(initialColumns); // Actual saved columns state
  const [draftColumns, setDraftColumns] = useState(columns); // Staging state for changes

  const updateUserDetails = async (values: TUser) => {
    try {
      const { isActive, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isActive: isActive ? 1 : 0,
        },
      };
      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      console.log("Finally");
    }
  };

  // To fetch the list of all the users
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          pageSize: PageSize,
          currentPage: 1,
        },
      };
      const response: TResponseModel = await post(GetUserList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TUser, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          fullName: item.fullName,
          emailId: item.emailId,
          mobileNumber: item.mobileNumber,
          roleUniqueId: item.roleUniqueId,
          roleName: item.roleName,
          designationTitle: item.designationTitle,
          createdOn: item.createdOn,
          status: item?.isActive ? "Active" : "In Active",
          isActive: item.isActive,
          id: item.id,
          customerName: item?.customerName,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TUser) => item.fullName)),
        ).map((fullName) => ({ text: fullName, value: fullName }));
        const emailFilters = Array.from(
          new Set(data.map((item: TUser) => item.emailId)),
        ).map((emailId) => ({ text: emailId, value: emailId }));
        const mobileNumberFilters = Array.from(
          new Set(data.map((item: TUser) => item.mobileNumber)),
        ).map((mobileNumber) => ({ text: mobileNumber, value: mobileNumber }));
        const userRoleNameFilters = Array.from(
          new Set(data.map((item: TUser) => item.roleName)),
        ).map((userRoleName) => ({
          text: userRoleName,
          value: userRoleName,
        }));
        const customerFilters = Array.from(
          new Set(data.map((item: TUser) => item?.customerName)),
        ).map((customerName) => ({
          text: customerName,
          value: customerName,
        }));
        const designationFilters = Array.from(
          new Set(
            data.map((item: TUser) => item?.designationTitle).filter(Boolean),
          ),
        ).map((designationTitle) => ({
          text: designationTitle,
          value: designationTitle,
        }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "fullName") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "emailId") {
              return { ...col, filters: emailFilters };
            }
            if (col.key === "mobileNumber") {
              return { ...col, filters: mobileNumberFilters };
            }
            if (col.key === "roleName") {
              return { ...col, filters: userRoleNameFilters };
            }

            if (col.key === "customerName") {
              return { ...col, filters: customerFilters };
            }
            if (col.key === "designationTitle") {
              return { ...col, filters: designationFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action",
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
    isActive: user.isActive ? "Active" : "Inactive",
    createdOn: DateUtils.format(user.createdOn, dateOnlyFormat),
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<TeamOutlined />}
        title={`${filterParams?.totalRows} Total Users`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDUSER,
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={UserForm}
              componentProps={{
                id: 0,
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Add New User"
              size="large"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`users_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <Modal
        title="Confirm"
        open={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cancelText="No"
        onOk={() =>
          handleOk({
            ...selectedUser,
            isActive: !selectedUser?.isActive,
          } as any)
        }
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to {selectedUser?.isActive ? "Deactivate" : "Activate"}{" "}
          <b>{selectedUser?.fullName}</b>
        </p>
      </Modal>
      <Modal
        title="Confirm Delete"
        open={isDeleteModalVisible}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
      >
        <p>
          Are you about to delete <b>{selectedUser?.fullName}</b>?
        </p>
      </Modal>
    </div>
  );
}
